import React, { useRef, useState } from "react";
import { useDeal } from "../lib/store";
import * as m from "../lib/model";
import { Card, Logo } from "../components/shared";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import { FileDown, RotateCcw } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
export const DEFAULT_BB = {
  borrower: "", lender: "", certDate: today(), arDate: "", invDate: "", agreementDate: "", agreementSection: "",
  totalAR: 0, over90: 0, crossAged: 0, concentration: 0, concentrationPct: 20, otherIneligibleAR: 0, arAdvance: 90,
  totalInv: 0, ineligibleInv: 0, invAdvance: 60,
  loanBalance: 0, commitment: 0, signerName: "", signerTitle: "",
};

export function bbCalc(b) {
  const ineligAR = (b.over90 || 0) + (b.crossAged || 0) + (b.concentration || 0) + (b.otherIneligibleAR || 0);
  const eligAR = Math.max(0, (b.totalAR || 0) - ineligAR);
  const marginAR = eligAR * ((b.arAdvance || 0) / 100);
  const eligInv = Math.max(0, (b.totalInv || 0) - (b.ineligibleInv || 0));
  const marginInv = eligInv * ((b.invAdvance || 0) / 100);
  const gross = marginAR + marginInv;
  const base = b.commitment > 0 ? Math.min(gross, b.commitment) : gross;
  const available = base - (b.loanBalance || 0);
  return { ineligAR, eligAR, marginAR, eligInv, marginInv, gross, base, available, capped: b.commitment > 0 && gross > b.commitment };
}

const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-US") : "____________");
const neg = (n) => (n ? `(${m.fmt$(n)})` : "—");

export default function BorrowingBase() {
  const { M, setField } = useDeal();
  const b = { ...DEFAULT_BB, borrower: M.dealName || "", ...(M.borrowingBase || {}) };
  const c = bbCalc(b);
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setField("borrowingBase", { ...b, [k]: v });
  const num = (k) => ({ className: "inp mono", type: "number", value: b[k] || "", onChange: (e) => set(k, +e.target.value || 0), "data-testid": `bb-${k}` });
  const txt = (k, type = "text") => ({ className: "inp", type, value: b[k] || "", onChange: (e) => set(k, e.target.value), "data-testid": `bb-${k}` });

  const download = async () => {
    setBusy(true);
    const tid = toast.loading("Building certificate…");
    try {
      const node = ref.current;
      const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff", useCORS: true, windowWidth: node.scrollWidth });
      const pdf = new jsPDF({ unit: "pt", format: "letter", orientation: "portrait" });
      const pw = pdf.internal.pageSize.getWidth(), ph = pdf.internal.pageSize.getHeight();
      let w = pw, h = (canvas.height * pw) / canvas.width;
      if (h > ph) { const s = ph / h; h = ph; w = pw * s; }
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.95), "JPEG", (pw - w) / 2, 0, w, h);
      pdf.save(`Borrowing-Base-Certificate-${(b.borrower || "deal").replace(/\s+/g, "-")}-${b.certDate}.pdf`);
      toast.success("Certificate PDF downloaded", { id: tid });
    } catch { toast.error("Could not build the PDF", { id: tid }); }
    finally { setBusy(false); }
  };

  return (
    <div className="page" data-testid="borrowing-base-page">
      <div className="page-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="page-title">Borrowing Base Certificate</div>
          <div className="page-sub">Complete from the most recent A/R aging and balance sheet (both within 30 days). Saved with the deal.</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-sm" onClick={() => setField("borrowingBase", { ...DEFAULT_BB, borrower: M.dealName || "" })} data-testid="bb-reset"><RotateCcw size={12} style={{ marginRight: 4 }} />Reset</button>
          <button className="btn btn-primary" onClick={download} disabled={busy} data-testid="bb-download"><FileDown size={13} style={{ marginRight: 6 }} />{busy ? "Building…" : "Download PDF"}</button>
        </div>
      </div>

      <div className="bb-layout">
        <div className="bb-form">
          <Card title="Parties & Dates">
            <div className="bb-rows">
              <label className="bb-row"><span>Borrower</span><input {...txt("borrower")} /></label>
              <label className="bb-row"><span>Lender / Credit Union</span><input {...txt("lender")} placeholder="ABC Credit Union" /></label>
              <label className="bb-row"><span>Certificate date</span><input {...txt("certDate", "date")} /></label>
              <label className="bb-row"><span>Loan agreement date</span><input {...txt("agreementDate", "date")} /></label>
              <label className="bb-row"><span>Agreement section</span><input {...txt("agreementSection")} placeholder="e.g. 2.4" /></label>
            </div>
          </Card>
          <Card title="Accounts Receivable Aging Summary">
            <label className="bb-row"><span>A/R aging date</span><input {...txt("arDate", "date")} /></label>
            <div className="bb-rows">
              <label className="bb-row"><span>Total accounts receivable</span><input {...num("totalAR")} /></label>
              <label className="bb-row"><span>Amounts over 90 days</span><input {...num("over90")} /></label>
              <label className="bb-row"><span>Cross-aged (accounts &gt;50% over 90)</span><input {...num("crossAged")} /></label>
              <label className="bb-row"><span>Concentration limit %</span><input {...num("concentrationPct")} /></label>
              <label className="bb-row"><span>Amounts exceeding concentration</span><input {...num("concentration")} /></label>
              <label className="bb-row"><span>Other ineligible A/R</span><input {...num("otherIneligibleAR")} /></label>
              <label className="bb-row"><span>A/R advance rate %</span><input {...num("arAdvance")} /></label>
            </div>
            <div className="bb-live"><span>Eligible receivables</span><b className="mono">{m.fmt$(c.eligAR)}</b><span>Margined</span><b className="mono pos">{m.fmt$(c.marginAR)}</b></div>
          </Card>
          <Card title="Inventory Summary">
            <label className="bb-row"><span>Balance sheet / valuation date</span><input {...txt("invDate", "date")} /></label>
            <div className="bb-rows">
              <label className="bb-row"><span>Total inventory</span><input {...num("totalInv")} /></label>
              <label className="bb-row"><span>Ineligible inventory (WIP, perishable, consigned, obsolete)</span><input {...num("ineligibleInv")} /></label>
              <label className="bb-row"><span>Inventory advance rate %</span><input {...num("invAdvance")} /></label>
            </div>
            <div className="bb-live"><span>Eligible inventory</span><b className="mono">{m.fmt$(c.eligInv)}</b><span>Margined</span><b className="mono pos">{m.fmt$(c.marginInv)}</b></div>
          </Card>
          <Card title="Loan & Certification">
            <div className="bb-rows">
              <label className="bb-row"><span>Maximum commitment</span><input {...num("commitment")} /></label>
              <label className="bb-row"><span>Current loan balance</span><input {...num("loanBalance")} /></label>
              <label className="bb-row"><span>Signer name</span><input {...txt("signerName")} /></label>
              <label className="bb-row"><span>Signer title</span><input {...txt("signerTitle")} placeholder="Managing Partner" /></label>
            </div>
            <div className="bb-live"><span>Borrowing base</span><b className="mono">{m.fmt$(c.base)}</b><span>Available</span><b className={`mono ${c.available >= 0 ? "pos" : "neg"}`} data-testid="bb-available">{m.fmt$(c.available)}</b></div>
            {c.capped && <div className="bullet" style={{ marginTop: 8 }}>Margined collateral ({m.fmt$(c.gross)}) exceeds the commitment — borrowing base capped at {m.fmt$(b.commitment)}.</div>}
            {c.available < 0 && <div className="bullet" style={{ color: "#e88a8a" }}>Over-advance of {m.fmt$(-c.available)} — loan balance exceeds the borrowing base.</div>}
          </Card>
        </div>

        <div className="bb-preview-wrap">
          <div className="bb-paper teaser" ref={ref} data-testid="bb-preview">
            <div className="t-hero">
              <div>
                <div className="t-eyebrow">Borrowing Base Certificate</div>
                <h1>{b.borrower || M.dealName || "Borrower"}</h1>
                <div className="t-sub">{[M.companyName && M.companyName !== b.borrower ? M.companyName : null, M.industry || M.businessType, M.address].filter(Boolean).join(" · ")}</div>
                <div className="t-sub" style={{ marginTop: 6 }}>Prepared for <b style={{ color: "#fff" }}>{b.lender || "Lender"}</b> · Certificate date {fmtDate(b.certDate)}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <Logo height={52} />
                <div className="t-contact">{M.analystContact || M.analystName || "XYZ Ventures"}{M.analystEmail ? <><br />{M.analystEmail}</> : null}{M.analystPhone ? <><br />{M.analystPhone}</> : null}<br />Confidential</div>
              </div>
            </div>
            <div className="bb-body">
              <p className="bb-note">Completed from the most recent Accounts Receivable Aging Summary and Balance Sheet (both within the past 30 days).</p>

              <div className="bb-sec">
                <div className="bb-sec-head"><h2>Accounts Receivable Aging Summary</h2><span className="bb-date">As of {fmtDate(b.arDate)}</span></div>
                <table className="bb-tbl">
                  <tbody>
                    <tr><td>Total Accounts Receivable</td><td className="n">{m.fmt$(b.totalAR)}</td></tr>
                    <tr className="less"><td>Less: Ineligible Amounts</td><td></td></tr>
                    <tr><td className="ind">Amounts over 90 days</td><td className="n neg">{neg(b.over90)}</td></tr>
                    <tr><td className="ind">Accounts with &gt;50% over 90 days (cross-aged)</td><td className="n neg">{neg(b.crossAged)}</td></tr>
                    <tr><td className="ind">Amounts exceeding {b.concentrationPct}% concentration limit</td><td className="n neg">{neg(b.concentration)}</td></tr>
                    {b.otherIneligibleAR > 0 && <tr><td className="ind">Other ineligible receivables</td><td className="n neg">{neg(b.otherIneligibleAR)}</td></tr>}
                    <tr className="sub"><td>Total Eligible Receivables</td><td className="n">{m.fmt$(c.eligAR)}</td></tr>
                    <tr><td>Advance rate (not to exceed {b.arAdvance}%)</td><td className="n">{b.arAdvance}%</td></tr>
                    <tr className="tot"><td>Total Margined Receivables</td><td className="n">{m.fmt$(c.marginAR)}</td></tr>
                  </tbody>
                </table>
              </div>

              <div className="bb-sec">
                <div className="bb-sec-head"><h2>Inventory Summary</h2><span className="bb-date">Valuation / balance sheet {fmtDate(b.invDate)}</span></div>
                <table className="bb-tbl">
                  <tbody>
                    <tr><td>Total Inventory</td><td className="n">{m.fmt$(b.totalInv)}</td></tr>
                    <tr><td>Less: Ineligible inventory <small>(work in progress, perishable, consigned, obsolete)</small></td><td className="n neg">{neg(b.ineligibleInv)}</td></tr>
                    <tr className="sub"><td>Eligible Inventory</td><td className="n">{m.fmt$(c.eligInv)}</td></tr>
                    <tr><td>Advance rate (not to exceed {b.invAdvance}%)</td><td className="n">{b.invAdvance}%</td></tr>
                    <tr className="tot"><td>Total Margined Inventory</td><td className="n">{m.fmt$(c.marginInv)}</td></tr>
                  </tbody>
                </table>
              </div>

              <div className="bb-sec bb-avail">
                <table className="bb-tbl">
                  <tbody>
                    <tr><td>Total Margined Collateral</td><td className="n">{m.fmt$(c.gross)}</td></tr>
                    {b.commitment > 0 && <tr><td>Maximum commitment{c.capped ? " (borrowing base capped)" : ""}</td><td className="n">{m.fmt$(b.commitment)}</td></tr>}
                    <tr className="sub"><td>Available Borrowing Base</td><td className="n">{m.fmt$(c.base)}</td></tr>
                    <tr><td>Less: Current loan balance</td><td className="n neg">{neg(b.loanBalance)}</td></tr>
                    <tr className="tot big"><td>Available Funds{c.available < 0 ? " — Over-advance" : ""}</td><td className={`n ${c.available < 0 ? "neg" : "pos"}`} data-testid="bb-preview-available">{m.fmt$(c.available)}</td></tr>
                  </tbody>
                </table>
              </div>

              <p className="bb-cert">For purposes of securing credit from {b.lender || "________________"}, the undersigned hereby certifies that the above information is true and correct in all material respects. This certificate is made and given in compliance with Section {b.agreementSection || "X.X"} of the Loan Agreement dated {b.agreementDate ? fmtDate(b.agreementDate) : "____________"}.</p>
              <div className="bb-sign">
                <div><span className="k">By</span><span className="line">{b.signerName}</span></div>
                <div><span className="k">Title</span><span className="line">{b.signerTitle}</span></div>
                <div><span className="k">Date</span><span className="line short">{fmtDate(b.certDate)}</span></div>
              </div>
            </div>
            <div className="t-foot">XYZ Ventures · {b.borrower || M.dealName} · Borrowing Base Certificate · {fmtDate(b.certDate)} · Confidential</div>
          </div>
        </div>
      </div>
    </div>
  );
}
